/**
 * Customer PWA root error boundary — props aligned with monorepo
 * `ErrorBoundaryContract` (see `up-backend/docs/FRONTEND/ERROR_BOUNDARY_CONTRACT.md`).
 *
 * Customer, kiosk, and pickup apps use `fallback?: ReactNode` + `retryKey` remount.
 * Admin uses a function fallback variant documented separately.
 */
import type { ReactNode, ErrorInfo } from 'react';
import { Component } from 'react';
import { useTranslation } from 'react-i18next';
import { captureCustomerBoundaryError } from '../../observability/customerBoundarySentry.js';
import { getCustomerObservabilitySnapshot } from '../../observability/CustomerObservabilityContext.js';
import { customerLogger } from '../../logging/customerLogger.js';
import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';

/** Monorepo contract for customer, kiosk, and pickup error boundaries. */
export interface ErrorBoundaryContract {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

/** L1 tags from main; L2/L3 RemountBoundary may pass `feature` only (no MS-REC-076). */
export interface ErrorBoundaryObservability {
  readonly boundary_layer?: string;
  readonly msId?: string;
  readonly feature?: string;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  retryKey: number;
}

export type ErrorBoundaryProps = ErrorBoundaryContract & {
  readonly observability?: ErrorBoundaryObservability;
};

/**
 * Public-copy-only fallback. Never render `error.message`, `error.stack`, or
 * React `componentStack` — those stay in logs / Sentry via componentDidCatch.
 */
function DefaultFallback({
  onRetry,
}: {
  onRetry: () => void;
}): JSX.Element {
  const { t } = useTranslation('customer');
  return (
    <div className="mx-auto flex min-h-[50vh] w-full min-w-0 max-w-lg flex-col justify-center p-6">
      <Card padded className="w-full min-w-0" role="alert">
        <h2 className="text-lg font-semibold text-[var(--color-on-surface)]">
          {t('app.errorBoundary.title')}
        </h2>
        <p className="mt-2 text-sm text-[var(--color-on-surface-muted)]">
          {t('app.errorBoundary.message')}
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button type="button" intent="primary" size="md" onClick={onRetry}>
            {t('app.errorBoundary.retry')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function createLocalCorrelationId(): string | undefined {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return undefined;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, retryKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const obs = this.props.observability;
    const feature = obs?.feature ?? 'shell';
    const isL1 =
      obs?.boundary_layer === 'L1' || obs?.msId === 'MS-REC-076';
    const correlationId = isL1 ? createLocalCorrelationId() : undefined;

    customerLogger.error('ErrorBoundary caught an error', error, {
      module: 'ErrorBoundary',
      feature,
      operation: 'componentDidCatch',
      componentStack: errorInfo.componentStack,
      ...(obs?.boundary_layer !== undefined ? { boundary_layer: obs.boundary_layer } : {}),
      ...(obs?.msId !== undefined ? { msId: obs.msId } : {}),
      ...(correlationId !== undefined ? { correlation_id: correlationId } : {}),
    });

    captureCustomerBoundaryError(
      error,
      errorInfo,
      getCustomerObservabilitySnapshot(),
      {
        feature,
        ...(obs?.boundary_layer !== undefined ? { boundary_layer: obs.boundary_layer } : {}),
        ...(obs?.msId !== undefined ? { msId: obs.msId } : {}),
        ...(correlationId !== undefined ? { correlation_id: correlationId } : {}),
      },
    );
  }

  private handleRetry = (): void => {
    this.setState((prev) => ({
      hasError: false,
      error: undefined,
      retryKey: prev.retryKey + 1,
    }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return <DefaultFallback onRetry={this.handleRetry} />;
    }
    // flex-1 min-h-0: pass #main remaining-height chain to route screens (e.g. map directory G5).
    return (
      <div key={this.state.retryKey} className="flex min-h-0 min-w-0 flex-1 flex-col">
        {this.props.children}
      </div>
    );
  }
}
